import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Palette,
  Upload,
  Image,
  Type,
  Droplet,
  Save,
  Loader2,
  Lock,
  Crown,
  Eye,
  Check,
  Globe,
  ChevronRight,
  Info,
  Code,
  FileCode,
  Settings,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../ui/accordion';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Preset accent colors
const PRESET_COLORS = [
  { value: '#F59E0B', label: 'Amber' },
  { value: '#10B981', label: 'Emerald' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#8B5CF6', label: 'Violet' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#EF4444', label: 'Red' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#6366F1', label: 'Indigo' },
];

/**
 * Branding & White Label Section
 * 
 * Visual branding workspace focused on:
 * - Brand Name
 * - Logo, Wordmark, Favicon
 * - Accent Color
 * - Portal/Email/Login Branding toggles
 * - Hide "Powered By" badge
 * 
 * Domain setup is handled in Domains & Redirects section.
 * Advanced code fields (Custom JS/CSS) are gated behind advanced/full white-label entitlement.
 * 
 * Access rules:
 * 1. Customer agencies: Requires white_label entitlement
 * 2. Internal agencies: Have internal override for testing/demo
 * 3. Settings control: Platform admin can lock/hide regardless of entitlement
 */
const BrandingWhiteLabelSection = ({ 
  agency, 
  agencyId, 
  entitlements, 
  isReadOnly, 
  onUpdate,
  internalOverrides = {},
  isInternalAgency = false,
  navigateToSection,
}) => {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Check if white-label is entitled (customer entitlement)
  const hasCustomerEntitlement = entitlements?.white_label && entitlements.white_label !== 'hidden';
  
  // Check for internal override (only for internal agencies)
  const hasInternalOverride = isInternalAgency && (
    internalOverrides?.white_label_full || 
    internalOverrides?.white_label_starter
  );
  
  // Effective access: internal override OR customer entitlement
  const isWhiteLabelEntitled = hasCustomerEntitlement || hasInternalOverride;
  const isLocked = entitlements?.white_label === 'locked' && !hasInternalOverride;
  
  // Check for ADVANCED/FULL white label entitlement (for code fields)
  // Starter White Label does NOT include Custom JS/CSS
  const hasAdvancedWhiteLabel = (
    entitlements?.white_label === 'full_access' &&
    entitlements?.advanced_white_label === 'full_access'
  ) || (
    isInternalAgency && internalOverrides?.white_label_full
  );
  
  // Form state - Basic branding fields
  const [formData, setFormData] = useState({
    brand_name: agency?.branding?.brand_name || agency?.name || '',
    accent_color: agency?.branding?.accent_color || '#F59E0B',
    logo_url: agency?.branding?.logo_url || '',
    wordmark_url: agency?.branding?.wordmark_url || '',
    favicon_url: agency?.branding?.favicon_url || '',
    enable_portal_branding: agency?.branding?.enable_portal_branding ?? true,
    enable_email_branding: agency?.branding?.enable_email_branding ?? true,
    enable_login_branding: agency?.branding?.enable_login_branding ?? false,
    hide_powered_by: agency?.branding?.hide_powered_by ?? false,
    // Policy URLs (included in Starter White Label as public-facing fields)
    privacy_policy_url: agency?.branding?.privacy_policy_url || '',
    terms_conditions_url: agency?.branding?.terms_conditions_url || '',
    // Advanced fields (gated behind advanced/full white-label)
    custom_css: agency?.branding?.custom_css || '',
    custom_js: agency?.branding?.custom_js || '',
    api_domain: agency?.branding?.api_domain || '',
    google_login_enabled: agency?.branding?.google_login_enabled ?? false,
  });
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSave = async () => {
    if (isReadOnly || isLocked) return;
    
    // Only save advanced fields if entitled
    const saveData = { ...formData };
    if (!hasAdvancedWhiteLabel) {
      delete saveData.custom_css;
      delete saveData.custom_js;
      delete saveData.api_domain;
      delete saveData.google_login_enabled;
    }
    
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/agencies/${agencyId}/branding`,
        { branding: saveData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Branding settings saved');
      if (onUpdate) onUpdate({ branding: saveData });
    } catch (err) {
      console.error('Failed to save branding:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  // Show locked state if not entitled
  if (!isWhiteLabelEntitled) {
    return (
      <div className="space-y-6" data-testid="branding-section-locked">
        {/* Section Header */}
        <div className="border-b border-slate-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Palette className="text-purple-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Branding & White Label</h2>
              <p className="text-sm text-slate-500">
                Customize your agency's branding
              </p>
            </div>
          </div>
        </div>
        
        {/* Locked State */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Lock className="text-slate-400" size={32} />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            White Label Not Available
          </h3>
          <p className="text-slate-500 mb-4 max-w-md mx-auto">
            Custom branding and white-label features require a plan upgrade. 
            Contact your account manager to unlock these capabilities.
          </p>
          <Badge className="bg-amber-100 text-amber-700 border-0">
            <Crown size={12} className="mr-1" />
            Upgrade Required
          </Badge>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="branding-section">
      {/* Internal Test Access Banner */}
      {hasInternalOverride && !hasCustomerEntitlement && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <Eye className="text-violet-600" size={20} />
          </div>
          <div>
            <p className="text-sm font-semibold text-violet-800">Internal Test Access</p>
            <p className="text-xs text-violet-600">
              This feature is available for QA/demo testing. Customer agencies require a plan upgrade to access.
            </p>
          </div>
          <Badge className="bg-violet-100 text-violet-700 border-0 ml-auto">
            Internal Override
          </Badge>
        </div>
      )}
      
      {/* Section Header */}
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Palette className="text-purple-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Branding & White Label</h2>
              <p className="text-sm text-slate-500">
                Customize your agency's visual branding
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {hasInternalOverride && !hasCustomerEntitlement && (
              <Badge className="bg-violet-100 text-violet-700 border-0">
                Test Mode
              </Badge>
            )}
            <Badge className="bg-emerald-100 text-emerald-700 border-0">
              <Check size={12} className="mr-1" />
              White Label Enabled
            </Badge>
          </div>
        </div>
      </div>
      
      {/* Starter White Label Info */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
            <Info className="text-purple-600" size={18} />
          </div>
          <div>
            <h4 className="font-semibold text-purple-900 mb-1">Starter White Label</h4>
            <p className="text-sm text-purple-800 mb-3">
              Configure your brand identity for the client-facing portal. These settings 
              apply to your branded client portal.
            </p>
            <div className="flex flex-wrap gap-4 text-xs text-purple-700">
              <div className="flex items-center gap-1">
                <Check size={12} />
                <span>Brand Name & Logo</span>
              </div>
              <div className="flex items-center gap-1">
                <Check size={12} />
                <span>Accent Colors</span>
              </div>
              <div className="flex items-center gap-1">
                <Check size={12} />
                <span>Portal Branding</span>
              </div>
              <div className="flex items-center gap-1">
                <Check size={12} />
                <span>Email Branding</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Manage Domains CTA */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <Globe className="text-teal-600" size={18} />
            </div>
            <div>
              <p className="font-medium text-slate-900">Custom Domain Setup</p>
              <p className="text-sm text-slate-500">
                Connect your custom domain for the client portal
              </p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={() => navigateToSection && navigateToSection('domains')}
            data-testid="manage-domains-btn"
          >
            <ChevronRight size={16} className="mr-1" />
            Manage Domains
          </Button>
        </div>
      </div>
      
      {/* Brand Identity */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Brand Identity
        </h3>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="brand_name">
              <Type size={14} className="inline mr-1" />
              Brand Name
            </Label>
            <Input
              id="brand_name"
              value={formData.brand_name}
              onChange={(e) => handleChange('brand_name', e.target.value)}
              placeholder="Your Brand Name"
              disabled={isReadOnly || isLocked}
              className="bg-white"
              data-testid="input-brand-name"
            />
            <p className="text-xs text-slate-500">
              This name will appear throughout the client portal
            </p>
          </div>
          
          {/* Logo Upload */}
          <div className="grid grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Agency Logo</Label>
              <div className="w-24 h-24 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                {formData.logo_url ? (
                  <img src={formData.logo_url} alt="Logo" className="w-full h-full object-contain rounded-xl" />
                ) : (
                  <Image className="text-slate-400" size={32} />
                )}
              </div>
              <Button variant="outline" size="sm" disabled={isReadOnly || isLocked} data-testid="upload-logo-btn">
                <Upload size={14} className="mr-2" />
                Upload
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label>Wordmark</Label>
              <div className="w-32 h-16 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                {formData.wordmark_url ? (
                  <img src={formData.wordmark_url} alt="Wordmark" className="w-full h-full object-contain rounded-xl" />
                ) : (
                  <Type className="text-slate-400" size={24} />
                )}
              </div>
              <Button variant="outline" size="sm" disabled={isReadOnly || isLocked} data-testid="upload-wordmark-btn">
                <Upload size={14} className="mr-2" />
                Upload
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label>Favicon</Label>
              <div className="w-12 h-12 rounded-lg bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                {formData.favicon_url ? (
                  <img src={formData.favicon_url} alt="Favicon" className="w-full h-full object-contain rounded-lg" />
                ) : (
                  <Image className="text-slate-400" size={16} />
                )}
              </div>
              <Button variant="outline" size="sm" disabled={isReadOnly || isLocked} data-testid="upload-favicon-btn">
                <Upload size={14} className="mr-2" />
                Upload
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Accent Color */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          <Droplet size={14} className="inline mr-1" />
          Accent Color
        </h3>
        
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-xl border-2 border-slate-200"
            style={{ backgroundColor: formData.accent_color }}
          />
          <Input
            type="color"
            value={formData.accent_color}
            onChange={(e) => handleChange('accent_color', e.target.value)}
            className="w-16 h-10 p-1 cursor-pointer"
            disabled={isReadOnly || isLocked}
            data-testid="input-accent-color"
          />
          <Input
            value={formData.accent_color}
            onChange={(e) => handleChange('accent_color', e.target.value)}
            placeholder="#F59E0B"
            className="w-32 bg-white font-mono"
            disabled={isReadOnly || isLocked}
            data-testid="input-accent-hex"
          />
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {PRESET_COLORS.map(color => (
            <button
              key={color.value}
              onClick={() => handleChange('accent_color', color.value)}
              disabled={isReadOnly || isLocked}
              className={`w-8 h-8 rounded-lg border-2 transition-all ${
                formData.accent_color === color.value 
                  ? 'border-slate-900 scale-110' 
                  : 'border-transparent hover:scale-105'
              }`}
              style={{ backgroundColor: color.value }}
              title={color.label}
              data-testid={`color-${color.label.toLowerCase()}`}
            />
          ))}
        </div>
      </div>
      
      {/* Branding Options */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Branding Options
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900 text-sm">Portal Branding</p>
              <p className="text-xs text-slate-500">Apply branding to client portal</p>
            </div>
            <Switch
              checked={formData.enable_portal_branding}
              onCheckedChange={(checked) => handleChange('enable_portal_branding', checked)}
              disabled={isReadOnly || isLocked}
              data-testid="switch-portal-branding"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900 text-sm">Email Branding</p>
              <p className="text-xs text-slate-500">Apply branding to email templates</p>
            </div>
            <Switch
              checked={formData.enable_email_branding}
              onCheckedChange={(checked) => handleChange('enable_email_branding', checked)}
              disabled={isReadOnly || isLocked}
              data-testid="switch-email-branding"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900 text-sm">Login Page Branding</p>
              <p className="text-xs text-slate-500">Custom branding on login screen</p>
            </div>
            <Switch
              checked={formData.enable_login_branding}
              onCheckedChange={(checked) => handleChange('enable_login_branding', checked)}
              disabled={isReadOnly || isLocked}
              data-testid="switch-login-branding"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900 text-sm">Hide "Powered By" Badge</p>
              <p className="text-xs text-slate-500">Remove FundElite branding from footer</p>
            </div>
            <Switch
              checked={formData.hide_powered_by}
              onCheckedChange={(checked) => handleChange('hide_powered_by', checked)}
              disabled={isReadOnly || isLocked}
              data-testid="switch-hide-powered-by"
            />
          </div>
        </div>
      </div>
      
      {/* Policy URLs (Included in Starter White Label) */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Legal & Compliance URLs
        </h3>
        <p className="text-xs text-slate-500">
          Display your legal policies on the client portal
        </p>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="privacy_policy_url">Privacy Policy URL</Label>
            <Input
              id="privacy_policy_url"
              type="url"
              value={formData.privacy_policy_url}
              onChange={(e) => handleChange('privacy_policy_url', e.target.value)}
              placeholder="https://youragency.com/privacy"
              disabled={isReadOnly || isLocked}
              className="bg-white"
              data-testid="input-privacy-url"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="terms_conditions_url">Terms & Conditions URL</Label>
            <Input
              id="terms_conditions_url"
              type="url"
              value={formData.terms_conditions_url}
              onChange={(e) => handleChange('terms_conditions_url', e.target.value)}
              placeholder="https://youragency.com/terms"
              disabled={isReadOnly || isLocked}
              className="bg-white"
              data-testid="input-terms-url"
            />
          </div>
        </div>
      </div>
      
      {/* Preview */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          <Eye size={14} className="inline mr-1" />
          Preview
        </h3>
        
        <div 
          className="rounded-xl border border-slate-200 p-6"
          style={{ backgroundColor: formData.accent_color + '10' }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: formData.accent_color }}
            >
              {formData.logo_url ? (
                <img src={formData.logo_url} alt="" className="w-6 h-6" />
              ) : (
                <span className="text-white font-bold">{formData.brand_name?.[0] || 'A'}</span>
              )}
            </div>
            <span className="text-lg font-semibold text-slate-900">{formData.brand_name || 'Your Brand'}</span>
          </div>
          
          <Button 
            className="text-white"
            style={{ backgroundColor: formData.accent_color }}
          >
            Sample Button
          </Button>
        </div>
      </div>
      
      {/* Advanced Developer Settings - Hidden by default for Starter White Label */}
      {hasAdvancedWhiteLabel ? (
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="advanced" className="border border-amber-200 rounded-xl bg-amber-50/50">
            <AccordionTrigger className="px-6 hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Code className="text-amber-600" size={16} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-amber-900">Advanced Developer Settings</p>
                  <p className="text-xs text-amber-700 font-normal">Custom CSS, JS, API Domain, SSO</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6 space-y-4">
              {/* API Domain */}
              <div className="space-y-2">
                <Label htmlFor="api_domain">
                  <Globe size={14} className="inline mr-1" />
                  API Domain
                </Label>
                <Input
                  id="api_domain"
                  value={formData.api_domain}
                  onChange={(e) => handleChange('api_domain', e.target.value)}
                  placeholder="api.youragency.com"
                  disabled={isReadOnly || isLocked}
                  className="bg-white font-mono"
                  data-testid="input-api-domain"
                />
                <p className="text-xs text-slate-500">
                  Custom API domain for white-labeled integrations
                </p>
              </div>
              
              {/* Google Login */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                <div>
                  <p className="font-medium text-slate-900 text-sm">Google Login / SSO</p>
                  <p className="text-xs text-slate-500">Enable Google authentication for clients</p>
                </div>
                <Switch
                  checked={formData.google_login_enabled}
                  onCheckedChange={(checked) => handleChange('google_login_enabled', checked)}
                  disabled={isReadOnly || isLocked}
                  data-testid="switch-google-login"
                />
              </div>
              
              {/* Custom CSS */}
              <div className="space-y-2">
                <Label htmlFor="custom_css">
                  <FileCode size={14} className="inline mr-1" />
                  Custom CSS
                </Label>
                <Textarea
                  id="custom_css"
                  value={formData.custom_css}
                  onChange={(e) => handleChange('custom_css', e.target.value)}
                  placeholder="/* Your custom CSS styles */"
                  disabled={isReadOnly || isLocked}
                  className="font-mono text-sm h-32 bg-white"
                  data-testid="textarea-custom-css"
                />
              </div>
              
              {/* Custom JS */}
              <div className="space-y-2">
                <Label htmlFor="custom_js">
                  <Code size={14} className="inline mr-1" />
                  Custom JavaScript
                </Label>
                <Textarea
                  id="custom_js"
                  value={formData.custom_js}
                  onChange={(e) => handleChange('custom_js', e.target.value)}
                  placeholder="// Your custom JavaScript code"
                  disabled={isReadOnly || isLocked}
                  className="font-mono text-sm h-32 bg-white"
                  data-testid="textarea-custom-js"
                />
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle size={14} className="text-amber-600 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    <strong>Warning:</strong> Custom JavaScript runs on every page load. 
                    Ensure your code is safe and well-tested.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : (
        /* Hidden Advanced Settings for Starter White Label */
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-5" data-testid="advanced-settings-hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Code className="text-slate-400" size={18} />
              </div>
              <div>
                <p className="font-medium text-slate-600">Advanced Developer Settings</p>
                <p className="text-xs text-slate-400">
                  Custom CSS, JS, API Domain, SSO configuration
                </p>
              </div>
            </div>
            <Badge className="bg-slate-100 text-slate-500 border-0">
              <Crown size={12} className="mr-1" />
              Full White Label
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-3 ml-13">
            Upgrade to Full White Label to access developer-level customization options.
          </p>
        </div>
      )}
      
      {/* Save Button */}
      {!isReadOnly && !isLocked && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-branding-btn"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BrandingWhiteLabelSection;
